/** Scale bar maths for a Web Mercator map whose world is 512 px wide at zoom 0 (the 2D renderer's convention). */

/** WGS84 equatorial circumference in metres. */
export const EARTH_CIRCUMFERENCE_M = 40_075_016.686;
const WORLD_PX_AT_ZOOM_0 = 512;
const MAX_LATITUDE = 85.051129;

/** Ground metres covered by one CSS pixel at this zoom and latitude. */
export function metresPerPixel(zoom: number, latitude: number): number {
  const lat = Math.max(-MAX_LATITUDE, Math.min(MAX_LATITUDE, latitude));
  return (EARTH_CIRCUMFERENCE_M * Math.cos((lat * Math.PI) / 180)) / (WORLD_PX_AT_ZOOM_0 * 2 ** zoom);
}

export interface ScaleBarSpec {
  /** Bar length in CSS pixels (never more than the maximum asked for). */
  widthPx: number;
  /** The distance the bar stands for, in metres: a 1, 2 or 5 times a power of ten. */
  metres: number;
  label: string;
}

/** Metric label: metres below a kilometre, kilometres from there on. */
export function formatDistance(metres: number): string {
  if (metres >= 1000) return `${trim(metres / 1000)} km`;
  return `${trim(metres)} m`;
}

function trim(n: number): string {
  return String(Number(n.toPrecision(6)));
}

/** The longest round distance (1, 2 or 5 x 10^n metres) whose bar fits in `maxWidthPx`. */
export function scaleBar(zoom: number, latitude: number, maxWidthPx = 100): ScaleBarSpec {
  const mpp = metresPerPixel(zoom, latitude);
  const span = mpp * maxWidthPx;
  const power = 10 ** Math.floor(Math.log10(span));
  const first = span / power;
  const step = first >= 5 ? 5 : first >= 2 ? 2 : 1;
  const metres = Number((step * power).toPrecision(6));
  return { widthPx: Math.round(metres / mpp), metres, label: formatDistance(metres) };
}
